import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ReplaySubject, map, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';

import { ModalVideoEffectsComponent } from '../../modal-video-effects/modal-video-effects.component';
import { BrowserService } from '../../services/browser.service';
import { DeviceService } from '../../services/device/device.service';
import { DevicePermissionService } from '../../services/device-permission/device-permission.service';
import { PermissionsService } from '../../services/permissions.service';

@Component({
  selector: 'app-welcome-preview',
  templateUrl: './welcome-preview.component.html',
  styleUrls: ['./welcome-preview.component.scss'],
  standalone: false,
})
export class WelcomePreviewComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public user?: User;
  @Input() public showSmartBadging?: boolean;
  @Input() public smartBadgingHasQuestions?: boolean;
  @Input() public showWelcomeDeviceSelectionForm?: boolean;
  @Input() public showOrientationStep?: boolean;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public socialStarted = false;
  public defaultSelected = 'default';

  // Variables to handle badge preview
  protected companyName?: string;
  protected userName?: string;
  protected userTitle?: string;
  protected aboutMe?: string;

  public constructor(
    public dialog: MatDialog,
    private deviceService: DeviceService,
    private permissionsService: PermissionsService,
    private devicePermissionService: DevicePermissionService,
    private browserService: BrowserService,
    private store: Store,
  ) {
    super();
    this.setDisplayName('WelcomePermissionsComponent');
  }

  public ngOnInit(): void {
    this.store
      .select(coreSelectors.getUserAndProfileAndAccountInfo)
      .pipe(
        map(({ company, user, userProfile }) => ({
          companyName: company?.companyName,
          title: userProfile?.title,
          aboutMe: userProfile?.description,
          firstName: user.user?.firstName,
          lastName: user.user?.lastName,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ aboutMe, companyName, firstName, lastName, title }) => {
        this.companyName = companyName ?? '';
        this.userTitle = title ?? '';
        this.aboutMe = aboutMe ?? '';
        this.userName = this.formatUserName(firstName, lastName);
      });
  }

  /**
   * Toggle functions
   */
  public clickedToggleVideo(): void {
    if (!this.disableCameraButton) {
      if (!this.videoPermissionGranted) {
        this.devicePermissionService.showExplanationModal(this.videoPermissionGranted, this.audioPermissionGranted);
        this.permissionsService.requestVideoPermission();
      } else {
        this.devicePermissionService.setVideoState();
      }
    }
  }

  public clickedToggleMic(): void {
    if (!this.audioPermissionGranted) {
      this.devicePermissionService.showExplanationModal(this.videoPermissionGranted, this.audioPermissionGranted);
      this.permissionsService.requestAudioPermission();
    } else {
      this.devicePermissionService.setAudioState();
    }
  }

  /**
   * Getters and setters
   */
  public get shouldShowAvatarVideo(): boolean {
    return this.devicePermissionService.shouldShowAvatarVideo();
  }

  public get shouldShowAvatarPicture(): boolean {
    return this.devicePermissionService.shouldShowAvatarPicture();
  }

  public get disableCameraButton(): boolean {
    return this.videoPermissionGranted && this.devicePermissionService.shouldCameraButtonDisabled();
  }

  public get disableVideoEffects(): boolean {
    return !this.deviceService.initialized || this.devicePermissionService.shouldDisableVideoEffects();
  }

  public get isVideoEnabled(): boolean {
    return this.devicePermissionService.isVideoEnabled;
  }

  public get isMicEnabled(): boolean {
    return this.devicePermissionService.isMicEnabled;
  }

  public get audioPermissionGranted(): boolean {
    return this.devicePermissionService.permission?.audioGranted;
  }

  public get videoPermissionGranted(): boolean {
    return this.devicePermissionService.permission?.videoGranted;
  }

  public get showVideoEnabledIcon(): boolean {
    return this.isVideoEnabled && this.videoPermissionGranted;
  }

  public get showMicEnabledIcon(): boolean {
    return this.isMicEnabled && this.audioPermissionGranted;
  }

  public onVideoPlaying = (): void => {
    this.devicePermissionService.onVideoPlaying();
  };

  public onVideoTimeout = (): void => {
    this.devicePermissionService.onVideoTimeout();
  };

  public handleVideoEffectsClick(): void {
    this.logUserAction('video-effects', 'click');
    this.dialog.open<ModalVideoEffectsComponent>(ModalVideoEffectsComponent);
  }

  protected get safariStyle() {
    if (!this.browserService.isSafari) {
      return;
    }
    return {
      height: 'auto',
    };
  }

  /* destroy calls */
  public async ngOnDestroy(): Promise<void> {
    try {
      await this.permissionsService.cleanAllStreams();
    } catch (e) {
      this.error('Error cleaning up streams:', e);
    }
    window.HubSpotConversations?.widget?.load();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public formatUserName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }
}
